import type SubscriptionEntity from '@thirsty-camel/hump-club/src/modules/subscription/subscription.entity'
import {
  Box,
  Button,
  Container,
  ContainerProps,
  Flex,
  Heading,
  IncrementInput,
  LinkButton,
  Price,
  Skeleton,
  Stack,
  StarRating,
  Text,
  Title,
  useToast,
  VisuallyHidden,
  useProductSubscriptionQuery,
  Icon,
  Image,
  Divider,
} from '@thirstycamel/ui'
import type { CoreProductEntity } from '@ts/core/src/modules/coreproduct/coreproduct.entity'
import { ProductType } from '@ts/core/src/modules/coreproduct/coreproduct.types'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { useApp } from '../../hooks/useApp'
import { useAuthHeaders } from '../../hooks/useAuthHeaders'
import { useUser } from '../../hooks/useUser'
import { useActions, useStore } from '../../store/hooks'
import { getNiceErrorMessage } from '../../utils/getNiceErrorMessage'
import ProductAvailability from '../ProductAvailability'
import ProductBadge from '../ProductBadge'
import ProductFavouriteStatus from '../ProductFavouriteStatus'
import ProductImagePreview from '../ProductImagePreview'
import { State } from '../../types/state'
import ProductSource from '../ProductSource'
import { shouldShow1kDayBadge } from '../ProductCard'
import { useRouter } from 'next/router'

export interface ProductOverviewProps extends ContainerProps {
  product: CoreProductEntity
  isFetching?: boolean
}

export const ProductOverview = ({ product, isFetching, ...restProps }: ProductOverviewProps) => {
  const { user, isLoggedIn } = useUser()
  const toast = useToast()
  const router = useRouter()

  const { isMobileApp, pushToken } = useApp()

  let {
    isFetching: isFetchingSubscription,
    isLoading,
    isSubscribed,
    isSubscribing,
    isUnsubscribing,
    productSubscribe,
    productUnsubscribe,
    ...rest
  } = useProductSubscriptionQuery<SubscriptionEntity>(
    {
      product: product?.id,
      user: user?.id,
      token: pushToken,
    },
    { enabled: isMobileApp, ...useAuthHeaders() },
  )

  const selectedStore = useStore(s => s.store.selectedStore)
  const cart = useStore(s => s.cart)
  const { addProductToCart, createCart } = useActions(s => s.cart)

  const location = useStore(s => s.location)

  const [quantity, setQuantity] = useState(1)
  const [isAddingToCart, setAddingToCart] = useState(false)
  const [adjustedPrice, setAdjustedPrice] = useState(product?.pricing?.price)
  const [adjustedOfferPrice, setAdjustedOfferPrice] = useState(product?.pricing?.price)
  const [adjustedSpecialPrice, setAdjustedSpecialPrice] = useState(product?.specialPricing?.price)

  const region = selectedStore?.region || location.region || 'VIC'
  const isCamelCard = region === 'SA' || region === 'NT'
  const specialPrice = product?.specialPricing?.price

  const showAddToCart =
    (product?.pricing != null || product?.specialPricing != null) &&
    !selectedStore?.externalURL &&
    (selectedStore?.isPickupReady || selectedStore?.isDeliveryReady)

  useEffect(() => {
    if (product?.pricing) {
      setAdjustedPrice(quantity * product?.pricing?.price)
    }

    if (product?.offerPrice) {
      setAdjustedOfferPrice(quantity * product?.offerPrice)
    }

    if (product?.specialPricing) {
      setAdjustedSpecialPrice(quantity * product?.specialPricing?.price)
    }
  }, [quantity, product])

  const handleAddToCart = useCallback(async () => {
    /* Prevent double-clicks */
    if (cart.isFetching) return

    setAddingToCart(true)

    /* If the user doesn't have a cart, create one for them. */
    if (!cart.cartId) await createCart()

    try {
      await addProductToCart({ product, qty: quantity })
    } catch (error) {
      toast({
        title: getNiceErrorMessage(error),
        status: 'error',
      })
    } finally {
      setAddingToCart(false)
    }
  }, [addProductToCart, product, cart.cartId, quantity, createCart])

  const isNew = product?.newUntil && new Date(product.newUntil).getTime() > new Date().getTime()
  const isNewActive = isNew && product?.newBadgeActiveRegions.includes(region as State)

  const hasCustomBadge =
    (product?.badge && product.badge.activeRegions.includes(region as State)) || false
  const isBundle = product?.type === ProductType.BUNDLE
  const isBundleProduct = router.query?.bundleGroup

  const isEveryDayValue =
    product?.specialPricing?.type == 'PRICELIST' && !product?.specialPricing?.isSpecial
  const isMemberOnly = product?.offerPrice
  const isSpecial = specialPrice && !isEveryDayValue

  const showEveryDayValuePrice =
    isEveryDayValue &&
    (!product?.offerPrice || product?.offerPrice >= product?.specialPricing?.price)

  const showMemberPrice = isMemberOnly && (!isEveryDayValue || specialPrice > product?.offerPrice)

  return (
    <Container pt={[6, , 10]} pb={[5, 5, 20]} {...restProps}>
      <Flex
        direction={['column', , 'row']}
        justify="center"
        alignItems={['center', , 'flex-start']}
      >
        <Stack width={[, , '50%']} spacing={8} mr={[, 4, 4, 10]} ml={[, 4, 0]}>
          <Box
            border="1px"
            borderColor="gray.200"
            borderRadius="lg"
            position="relative"
            textAlign="center"
          >
            {showEveryDayValuePrice ? (
              <ProductBadge type="everyday" />
            ) : isSpecial ? (
              <ProductBadge type="save" price={product?.pricing?.price - specialPrice} />
            ) : showMemberPrice ? (
              <ProductBadge type="member" />
            ) : hasCustomBadge ? (
              <ProductBadge type="image" src={product?.badge?.image.filename} />
            ) : shouldShow1kDayBadge(product) ? (
              <Box position="absolute" top={1} left={1} color="white" userSelect="none">
                <Image src={`/static/images/win1kaday.png`} width="100px" />
              </Box>
            ) : isNew && isNewActive ? (
              <ProductBadge type="new" />
            ) : isBundle ? (
              <ProductBadge type="bundle" />
            ) : null}
            <ProductImagePreview
              images={product?.media}
              isFetching={isFetching}
              // isNew={mockProduct.isNew}
              // deal={mockProduct.deal}
            />
          </Box>

          <Box>
            <Text>
              {product ? (
                product.productGroup.description || 'This product has no description.'
              ) : (
                <Stack w="100%">
                  <Skeleton height="1rem" width="100%" />
                  <Skeleton height="1rem" width="100%" />
                  <Skeleton height="1rem" width="100%" />
                  <Skeleton height="1rem" width="100%" />
                  <Skeleton height="1rem" width="25%" />
                </Stack>
              )}
            </Text>
          </Box>
        </Stack>

        <Stack
          width={[, , '50%']}
          spacing={8}
          py={5}
          pl={[, 4, , 6]}
          pr={[, 4, , 0]}
          align={['center', , 'flex-start']}
          textAlign={['center', , 'left']}
        >
          <Stack spacing={3} w="100%">
            <Flex justifyContent="space-between">
              <Stack spacing={1} maxWidth="320px" mx={['auto', null, 0]}>
                <Heading
                  fontSize={['xl', , 'lg']}
                  color="pink.500"
                  textTransform="uppercase"
                  aria-hidden
                >
                  {product?.productGroup.brand?.name || <Skeleton h="1rem" w="5rem" />}
                </Heading>

                <Title fontSize="md" textTransform="uppercase">
                  <VisuallyHidden>{product?.productGroup.brand?.name}</VisuallyHidden>
                  {product?.productGroup.name}
                  {!product && <Skeleton h="2rem" w="12rem" />}
                </Title>

                {!product ? (
                  <Skeleton h="1.5rem" w="8rem" />
                ) : (
                  <Stack
                    spacing={2}
                    isInline
                    alignItems="center"
                    justify={['center', , 'flex-start']}
                  >
                    <StarRating value={product?.productGroup?.rating || 0} />

                    <Text fontWeight="bold" color="blackAlpha.600" fontSize="sm">
                      ({product?.productGroup?.numberOfReviews || 0})
                    </Text>
                  </Stack>
                )}
              </Stack>

              {product?.id && <ProductFavouriteStatus productId={product.id} />}
            </Flex>
          </Stack>

          <Divider />

          {(showMemberPrice || product?.pricing || product?.specialPricing) && (
            <Stack spacing={4}>
              {showMemberPrice && (
                <Stack alignItems={'flex-end'}>
                  <Stack
                    backgroundColor="gray.50"
                    borderColor="gray.200"
                    borderWidth="2px"
                    py={2}
                    px={4}
                  >
                    <Flex justifyContent={['center', , 'space-between']} alignItems="center">
                      <Stack isInline alignItems={'center'}>
                        <Heading as={Price} color={'pink.500'} fontSize={['4xl', , '3xl']}>
                          {adjustedOfferPrice}
                        </Heading>

                        <ProductBadge type="member" position="relative" top={0} left={0} />
                      </Stack>

                      {!isLoggedIn && (
                        <Stack mt={[5, , 0]} width={['100%', , 'auto']}>
                          <LinkButton
                            variantColor="pink"
                            size="sm"
                            href={isCamelCard ? '/page/[slug]' : '/register'}
                            hrefAs={isCamelCard ? '/page/camel-card' : null}
                          >
                            JOIN FOR FREE
                          </LinkButton>
                        </Stack>
                      )}
                    </Flex>

                    <Text fontSize="xs" fontStyle="italic" color="gray.500">
                      Member pricing is applied at checkout and may be subject to offer specific
                      redemption terms and conditions. Member pricing is only available to Hump Club
                      loyalty members.
                    </Text>
                  </Stack>
                </Stack>
              )}

              {(product?.pricing || product?.specialPricing) && (
                <>
                  <Stack>
                    {showMemberPrice && (
                      <Heading fontSize="xs" color="gray.600">
                        NON MEMBER PRICE
                      </Heading>
                    )}

                    <Stack isInline align="center">
                      {product?.pricing && !product?.specialPricing && (
                        <Heading
                          as={Price}
                          color={showMemberPrice || specialPrice ? 'blackAlpha.500' : 'pink.500'}
                          textDecoration={specialPrice ? 'line-through' : ''}
                          fontSize={showMemberPrice ? 'md' : ['4xl', , '3xl']}
                        >
                          {adjustedPrice}
                        </Heading>
                      )}

                      {specialPrice && (
                        <Heading
                          fontSize={showMemberPrice ? 'md' : ['4xl', , '3xl']}
                          as={Price}
                          color={showMemberPrice ? 'gray.500' : 'pink.500'}
                        >
                          {adjustedSpecialPrice}
                        </Heading>
                      )}
                    </Stack>

                    <Text fontSize="sm" color="gray.500">
                      *prices may vary in {region === 'QLD' ? 'store' : 'regional areas'}
                    </Text>
                  </Stack>
                  <Divider />
                </>
              )}
            </Stack>
          )}

          <Stack spacing={4} isInline>
            {product?.productGroup?.products?.map(variant => (
              <LinkButton
                hrefAs={`/product/${product?.productGroup.slug}/${variant.slug}`}
                href={`/product/[group]/[slug]?slug=${variant.slug}&group=${product?.productGroup.slug}`}
                key={variant.id}
                variant="unstyled"
                prefetch
                bg="gray.50"
                color="pink.500"
                fontFamily="heading"
                textTransform="uppercase"
                px={6}
                minHeight={8}
                border="2px"
                borderColor={product.id === variant.id ? 'pink.500' : 'transparent'}
                borderRadius="lg"
                fontSize="sm"
                _hover={{ border: '2px', borderColor: 'pink.500' }}
                _focus={{ border: '2px', borderColor: 'pink.500', boxShadow: 'outline' }}
              >
                {variant.factorDescription}
              </LinkButton>
            ))}
          </Stack>

          {(showAddToCart || isBundleProduct) && (
            <Flex
              direction={['column', 'column', 'row']}
              justifyContent={['center', , 'flex-start']}
              width="100%"
            >
              <Box display={['none', 'none', 'block']}>
                <IncrementInput
                  defaultValue={1}
                  min={1}
                  max={20}
                  justifyContent="center"
                  onChangeValue={setQuantity}
                  isReadOnly={isAddingToCart}
                />
              </Box>

              <Box display={['block', 'block', 'none']}>
                <IncrementInput
                  justifyContent="center"
                  defaultValue={1}
                  size="lg"
                  min={1}
                  max={20}
                  onChangeValue={setQuantity}
                  isReadOnly={isAddingToCart}
                />
              </Box>

              <Button
                ml={[, , 5]}
                mt={[5, 5, 0]}
                onClick={handleAddToCart}
                variantColor="pink"
                loadingText="Loading"
                width={['100%', 'auto']}
                isLoading={isFetching || isAddingToCart}
                isDisabled={!selectedStore}
              >
                Add To {showAddToCart ? 'Cart' : 'Bundle'}
              </Button>
            </Flex>
          )}

          {(isMobileApp || selectedStore?.externalURL) && (
            <Divider />
          )}

          {isMobileApp && (
            <Box width="100%">
              <Stack width={['100%', 'auto']}>
                {isFetchingSubscription ? (
                  <Button isDisabled width="100%">
                    Checking for subscription..
                  </Button>
                ) : isSubscribing ? (
                  <Button isDisabled  width="100%">
                    Subscribing to product..
                  </Button>
                ) : isUnsubscribing ? (
                  <Button isDisabled  width="100%">
                    Unsubscribing to product..
                  </Button>
                ) : isSubscribed ? (
                  <Button width="100%" onClick={() => productUnsubscribe()} rightIcon="bell">
                    Unsubscribe me
                  </Button>
                ) : (
                  <Button
                    onClick={() => productSubscribe()}
                    variantColor="pink"
                    rightIcon="bell"
                    width="100%"
                  >
                    Notify me when on sale
                  </Button>
                )}
              </Stack>
            </Box>
          )}

          {selectedStore?.externalURL && (
            <Box>
              <LinkButton
                mt={[5, 5, 0]}
                variantColor="pink"
                width={['100%', 'auto']}
                isExternal
                href={`${selectedStore.externalURL}${
                  product?.santExternalID ? `/lines/${product.santExternalID}` : ''
                }`}
                isLoading={cart.isFetching}
              >
                Shop now
              </LinkButton>
            </Box>
          )}

          <Divider />

          <ProductSource product={product} />
        </Stack>
      </Flex>
    </Container>
  )
}

export default memo(ProductOverview)
